// Client-side numerology calculations (Pythagorean method).
// Keep in sync with supabase/functions/numerology/index.ts.

export function reduce(n: number): number {
  while (n > 9 && n !== 11 && n !== 22 && n !== 33) {
    n = String(n).split('').reduce((sum, d) => sum + Number(d), 0);
  }
  return n;
}

function letterValue(char: string): number {
  const code = char.toUpperCase().charCodeAt(0);
  if (code < 65 || code > 90) return 0;
  return ((code - 65) % 9) + 1;
}

const VOWELS = new Set(['A', 'E', 'I', 'O', 'U']);

function normalise(name: string): string {
  return name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z]/g, '');
}

export function calcLifePath(birthDate: string): number {
  return reduce(birthDate.replace(/\D/g, '').split('').reduce((s, d) => s + Number(d), 0));
}

export function calcDestiny(name: string): number {
  return reduce([...normalise(name)].reduce((s, c) => s + letterValue(c), 0));
}

export function calcSoulUrge(name: string): number {
  return reduce([...normalise(name)].filter(c => VOWELS.has(c)).reduce((s, c) => s + letterValue(c), 0));
}

export function calcPersonality(name: string): number {
  return reduce([...normalise(name)].filter(c => !VOWELS.has(c)).reduce((s, c) => s + letterValue(c), 0));
}

export function calcPersonalYear(birthDate: string, year: number): number {
  const [, bMonth, bDay] = birthDate.split('-').map(Number);
  const yearSum = reduce(String(year).split('').reduce((s, d) => s + Number(d), 0));
  return reduce(bDay + bMonth + yearSum);
}

export function calcPersonalMonth(py: number, month: number): number {
  return reduce(py + month);
}

export function calcPersonalDay(pm: number, day: number): number {
  return reduce(pm + day);
}

export function calcMissingNumbers(name: string): number[] {
  const present = new Set([...normalise(name)].map(c => letterValue(c)).filter(Boolean));
  return [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => !present.has(n));
}

export function calcPinnacles(birthDate: string): [number, number, number, number] {
  const [bYear, bMonth, bDay] = birthDate.split('-').map(Number);
  const m  = reduce(bMonth);
  const d  = reduce(bDay);
  const y  = reduce(String(bYear).split('').reduce((s, n) => s + Number(n), 0));
  const p1 = reduce(m + d);
  const p2 = reduce(d + y);
  const p3 = reduce(p1 + p2);
  const p4 = reduce(m + y);
  return [p1, p2, p3, p4];
}

export function calcChallenges(birthDate: string): [number, number, number, number] {
  const [bYear, bMonth, bDay] = birthDate.split('-').map(Number);
  const m  = reduce(bMonth);
  const d  = reduce(bDay);
  const y  = reduce(String(bYear).split('').reduce((s, n) => s + Number(n), 0));
  const c1 = Math.abs(m - d);
  const c2 = Math.abs(d - y);
  const c3 = Math.abs(c1 - c2);
  const c4 = Math.abs(m - y);
  return [c1, c2, c3, c4];
}

export interface AllNumbers {
  caminhoDeVida: number;
  destino:       number;
  alma:          number;
  personalidade: number;
  expressao:     number;
  impressao:     number;
  motivacao:     number;
  maturidade:    number;
  missao:        number;
  proposito:     number;
  anoPessoal:    number;
  mesPessoal:    number;
  diaPessoal:    number;
  pinnacles:     [number, number, number, number];
  challenges:    [number, number, number, number];
  missing:       number[];
}

export function computeAllNumbers(fullName: string, birthDate: string, today = new Date()): AllNumbers {
  const lp   = calcLifePath(birthDate);
  const dest = calcDestiny(fullName);
  const soul = calcSoulUrge(fullName);
  const pers = calcPersonality(fullName);
  const py   = calcPersonalYear(birthDate, today.getFullYear());
  const pm   = calcPersonalMonth(py, today.getMonth() + 1);
  const pd   = calcPersonalDay(pm, today.getDate());
  const [, , bDay] = birthDate.split('-').map(Number);

  return {
    caminhoDeVida: lp,
    destino:       dest,
    alma:          soul,
    personalidade: pers,
    expressao:     dest,          // Expression = all letters, same as Destiny in Pythagorean
    impressao:     reduce(dest + pers),
    motivacao:     soul,          // First vowel of each name part; approximated as Soul Urge
    maturidade:    reduce(lp + dest),
    missao:        reduce(bDay),
    proposito:     reduce(lp + dest + soul),
    anoPessoal:    py,
    mesPessoal:    pm,
    diaPessoal:    pd,
    pinnacles:     calcPinnacles(birthDate),
    challenges:    calcChallenges(birthDate),
    missing:       calcMissingNumbers(fullName),
  };
}

export function displayNumberForKey(key: string, nums: AllNumbers): string {
  switch (key) {
    case 'caminhoDeVida':    return String(nums.caminhoDeVida);
    case 'destino':          return String(nums.destino);
    case 'alma':             return String(nums.alma);
    case 'personalidade':    return String(nums.personalidade);
    case 'expressao':        return String(nums.expressao);
    case 'impressao':        return String(nums.impressao);
    case 'motivacao':        return String(nums.motivacao);
    case 'maturidade':       return String(nums.maturidade);
    case 'missao':           return String(nums.missao);
    case 'proposito':        return String(nums.proposito);
    case 'anoPessoal':       return String(nums.anoPessoal);
    case 'mesPessoal':       return String(nums.mesPessoal);
    case 'diaPessoal':       return String(nums.diaPessoal);
    case 'ciclosDeVida':     return nums.pinnacles.join(' · ');
    case 'desafios':         return nums.challenges.join(' · ');
    case 'pontosMaturidade': return nums.pinnacles.map((p, i) => `P${i + 1}: ${p}`).join(' · ');
    case 'licoesKarmicas':   return nums.missing.length > 0 ? nums.missing.join(', ') : 'nenhuma';
    default:                 return '';
  }
}

export const PT_MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export const PT_DAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export const PT_DAYS_FULL  = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export function getMondayOfWeek(date: Date): Date {
  const d   = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}
